package twcomponents

import apiclient.geoobjects.EventInvitation
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.MeetingAttendee
import apiclient.geoobjects.MeetingInvitationStatus
import apiclient.geoobjects.TaskState
import apiclient.users.UserProfileSummary
import apiclient.users.toUserProfileSummary
import auth.ApiUserStore
import com.tryformation.localization.Translatable
import data.objects.ActiveObjectStore
import data.objects.defaultEventReminders
import data.objects.views.directediting.ValueSelectionStore
import data.users.UserListStore
import dev.fritz2.core.RenderContext
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.Store
import dev.fritz2.core.invoke
import dev.fritz2.core.placeholder
import dev.fritz2.core.storeOf
import dev.fritz2.core.values
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Clock
import localization.TL
import localization.getString
import localization.getTranslationFlow
import localization.translate
import model.L
import model.isInternalAdminGroupAdminOrGroupOwner
import search.simpleUserProfileListEntry
import search.titleSubtitleStack
import theme.FormationIcons
import theme.FormationUIIcons
import utils.combineToInstant
import utils.extractDurationInMinutes
import utils.formatDateForDatePicker
import utils.formatDateForDatePickerFlow
import utils.formatTimeForTimePicker
import utils.formatTimeForTimePickerFlow
import utils.getColor
import utils.getIcon
import utils.getName
import utils.getSubtitle
import utils.getTitle
import utils.nextQuarterOfHour
import webcomponents.genericInput
import webcomponents.inputLabelWrapper
import webcomponents.smallSelectButton
import webcomponents.twContentScrollBox
import webcomponents.twEllipseText

fun twUserSelectModal(
    toggleShowModalStore: Store<Boolean>,
    title: Translatable? = null,
    titleAsFlow: Flow<String>? = flowOf("User Select"),
    selectedUserProfileSummaryStore: Store<UserProfileSummary?>? = null,
    additionalSelectedUserHandlers: List<SimpleHandler<UserProfileSummary?>>? = null,
    anyoneOption: Boolean = false,
) {
    twSimpleModalWithCloseHeader(
        title = title,
        titleAsFlow = titleAsFlow,
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full md:min-w-96 overflow-y-auto p-2",
            id = "user-select",
        ) {
            val apiUserStore: ApiUserStore by koinCtx.inject()
            val userListStore: UserListStore by koinCtx.inject()
            val userSearchInputQueryStore = storeOf("")
            val userFilteredResultsStore = storeOf(userListStore.current)

            val myUserId = apiUserStore.current.userId

            userSearchInputQueryStore.data handledBy { query ->
                userFilteredResultsStore.update(
                    userListStore.current?.filter { user ->
                        user.name.contains(query, true)
                    },
                )
            }
            // Search input to filter users
            twInputField(userSearchInputQueryStore) {
                twInputTextField {
                    doOnceWhenElementInDOM(domNode = domNode) {
                        domNode.focus()
                    }
                    placeholder("Type to search user...")
                }
            }
            // results list
            div(
                "flex flex-col w-full h-full md:h-80 gap-2 overflow-y-auto",
            ) {
                userFilteredResultsStore.data.render { userList ->
                    if (anyoneOption) {
                        div("flex flex-row w-full h-max items-center justify-between p-1 cursor-pointer hover:bg-gray-100 rounded-xl") {
                            simpleUserProfileListEntry(
                                UserProfileSummary(
                                    userId = "",
                                    firstName = TL.CardUserSelect.ANYONE.getString(),
                                    lastName = null,
                                    jobTitle = TL.CardUserSelect.ANYONE_DESCRIPTION.getString(),
                                ),
                                fallbackIcon = FormationIcons.UserGroup,
                            )
                            clicks handledBy {
                                selectedUserProfileSummaryStore?.update(null)
                                additionalSelectedUserHandlers?.let { handlers ->
                                    handlers.forEach { handler ->
                                        handler(null)
                                    }
                                }
                                optionalCloseHandlers?.let { handlers ->
                                    handlers.forEach {
                                        it.invoke()
                                    }
                                }
                                closeModal()
                            }
                        }
                    }
                    userList?.firstOrNull { myUserId == it.userId }?.let { myUser ->
                        div("flex flex-row w-full h-max items-center justify-between p-1 cursor-pointer hover:bg-gray-100 rounded-xl") {
                            simpleUserProfileListEntry(myUser.toUserProfileSummary())

                            clicks handledBy {
                                selectedUserProfileSummaryStore?.update(myUser.toUserProfileSummary())
                                additionalSelectedUserHandlers?.let { handlers ->
                                    handlers.forEach { handler ->
                                        handler(myUser.toUserProfileSummary())
                                    }
                                }
                                optionalCloseHandlers?.let { handlers ->
                                    handlers.forEach {
                                        it.invoke()
                                    }
                                }
                                closeModal()
                            }
                        }
                    }
                    userList?.filter { it.userId != myUserId }?.forEach { userEntry ->
                        div("flex flex-row w-full h-max items-center justify-between p-1 cursor-pointer hover:bg-gray-100 rounded-xl") {
                            simpleUserProfileListEntry(userEntry.toUserProfileSummary())
                            clicks handledBy {
                                selectedUserProfileSummaryStore?.update(userEntry.toUserProfileSummary())
                                additionalSelectedUserHandlers?.let { handlers ->
                                    handlers.forEach { handler ->
                                        handler(userEntry.toUserProfileSummary())
                                    }
                                }
                                optionalCloseHandlers?.let { handlers ->
                                    handlers.forEach {
                                        it.invoke()
                                    }
                                }
                                closeModal()
                            }
                        }
                    }
                }
            }
        }
    }
}

fun RenderContext.selectorIndicator(active: Boolean) {
    div("flex items-center justify-center grow-0 shrink-0 basis-8 w-8 h-8 rounded-full p-2") {
        className(
            if (active) {
                "text-formationWhite bg-formationBlack"
            } else {
                "border-2 border-formationBlack"
            },
        )
        if (active) {
            twIconMedium(FormationUIIcons.Check)
        }
    }
}

fun RenderContext.meetingStateIndicator(meetingState: MeetingInvitationStatus?) {
    div("flex items-center justify-center grow-0 shrink-0 basis-8 w-8 h-8 rounded-full p-2") {
        when (meetingState) {
            MeetingInvitationStatus.Pending -> {
                className("text-formationWhite bg-gray-500")
                twIconMedium(MeetingInvitationStatus.Pending.getIcon())
            }

            MeetingInvitationStatus.Accepted -> {
                className("text-formationBlack bg-green-500")
                twIconMedium(MeetingInvitationStatus.Accepted.getIcon())
            }

            MeetingInvitationStatus.Maybe -> {
                className("text-formationBlack bg-yellow-500")
                twIconMedium(MeetingInvitationStatus.Maybe.getIcon())
            }

            MeetingInvitationStatus.Rejected -> {
                className("text-formationBlack bg-red-500")
                twIconMedium(MeetingInvitationStatus.Rejected.getIcon())
            }

            else -> {
                className("border-2 border-gray-500")
            }
        }
    }
}

fun twManageAttendeeModal(toggleShowModalStore: Store<Boolean>) {
    twSimpleModalWithCloseHeader(
        titleAsFlow = flowOf("Meeting Participants"),
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full md:min-w-96 overflow-y-auto p-2",
            id = "meeting-attendeeSelect-modal",
        ) {
            val apiUserStore: ApiUserStore by koinCtx.inject()
            val activeObjectStore: ActiveObjectStore by koinCtx.inject()
            val attendees = activeObjectStore.map(GeoObjectDetails.L.attendees)
            val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)
            val userListStore: UserListStore by koinCtx.inject()
            val userSearchInputQueryStore = storeOf("")
            val userFilteredResultsStore = storeOf(userListStore.current)

            val selectedUsersStore: ValueSelectionStore<UserProfileSummary> = ValueSelectionStore(
                activeObjectStore.current.attendees.orEmpty().mapNotNull { attendee ->
                    userListStore.getPublicUserProfile(attendee.userId)?.toUserProfileSummary()
                }.toSet(),
            )

            val attendeesStore: Store<Map<String, MeetingAttendee>>? = storeOf(
                activeObjectStore.current.attendees.orEmpty().mapNotNull { attendee ->
                    userListStore.getPublicUserProfile(attendee.userId)?.toUserProfileSummary()?.let { profile ->
                        profile.userId to attendee
                    }
                }.toMap(),
                Job(),
            )

            owner.data.render { owner ->
                val currentUser = apiUserStore.current
                val canChangeAttendees = owner == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner()


                val myUserId = apiUserStore.current.userId

                userSearchInputQueryStore.data handledBy { query ->
                    userFilteredResultsStore.update(
                        userListStore.current?.filter { user ->
                            user.name.contains(query, true)
                        },
                    )
                }

                twRowOf {
                    // Search input to filter users
                    twInputField(userSearchInputQueryStore) {
                        twInputTextField {
                            doOnceWhenElementInDOM(domNode = domNode) {
                                domNode.focus()
                            }
                            placeholder("Type to search user...")
                        }
                    }
                    if (canChangeAttendees) {
                        // SelectAll button
                        val allUsers = userListStore.current?.map { it.toUserProfileSummary() }.orEmpty()
                        val allSelected = selectedUsersStore.data.map { selectedUsers ->
                            selectedUsers != null
                                && selectedUsers.containsAll(allUsers)
                        }
                        allSelected.render { active ->
                            button("mr-1") {
                                selectorIndicator(active)
                                clicks handledBy {
                                    selectedUsersStore.addAllValues(
                                        userListStore.current?.map { it.toUserProfileSummary() }.orEmpty(),
                                    )
                                }
                            }
                        }
                    }
                }

                // results list
                div(
                    "flex flex-col w-full h-full md:h-80 gap-2 overflow-y-auto",
                ) {
                    userFilteredResultsStore.data.render { userList ->

                        val displayedUsers = if (canChangeAttendees) {
                            userList?.sortedByDescending { user -> attendeesStore?.current?.let { user.userId in it.keys } }
                        } else {
                            userList?.filter { user -> attendeesStore?.current?.let { user.userId in it.keys } ?: false }
                        }

                        displayedUsers?.firstOrNull { myUserId == it.userId }?.let { myUser ->
                            div("flex flex-row w-full h-max items-center justify-between p-1 gap-2 rounded-xl") {
                                simpleUserProfileListEntry(myUser.toUserProfileSummary())
                                attendeesStore?.current[myUser.userId]?.meetingInvitationStatus?.let { meetingState ->
                                    meetingStateIndicator(meetingState)
                                }
                                if (canChangeAttendees) {
                                    className("cursor-pointer hover:bg-gray-100")
                                    selectedUsersStore.data.render { selectedUsers ->
                                        val active = myUser.userId in selectedUsers?.map { it.userId }.orEmpty()
                                        selectorIndicator(active)
                                    }
                                    clicks handledBy {
                                        selectedUsersStore.addOrRemoveValue(myUser.toUserProfileSummary())
                                        optionalCloseHandlers?.let { handlers ->
                                            handlers.forEach {
                                                it.invoke()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        displayedUsers?.filter { it.userId != myUserId }?.forEach { userEntry ->
                            div("flex flex-row w-full h-max items-center justify-between p-1 gap-2 rounded-xl") {
                                simpleUserProfileListEntry(userEntry.toUserProfileSummary())
                                attendeesStore?.current[userEntry.userId]?.meetingInvitationStatus?.let { meetingState ->
                                    meetingStateIndicator(meetingState)
                                }
                                if (canChangeAttendees) {
                                    className("cursor-pointer hover:bg-gray-100")
                                    selectedUsersStore.data.render { selectedUsers ->
                                        val active = userEntry.userId in selectedUsers?.map { it.userId }.orEmpty()
                                        selectorIndicator(active)
                                    }
                                    clicks handledBy {
                                        selectedUsersStore.addOrRemoveValue(userEntry.toUserProfileSummary())
                                        optionalCloseHandlers?.let { handlers ->
                                            handlers.forEach {
                                                it.invoke()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                if (canChangeAttendees) {
                    twPrimaryButton(
                        fallbackTextAsFlow = flowOf("Update Participants"),
                        disabledFlow = combine(
                            attendees.data,
                            selectedUsersStore.data,
                        ) { a, s ->
                            a?.map { it.userId }.orEmpty().toSet() == s?.map { it.userId }.orEmpty().toSet()
                        },
                    ) {
                        clicks handledBy {
                            val initialAttendeeIds = attendees.current.orEmpty().map { attendee -> attendee.userId }
                            val currentSelectedAttendeeIds =
                                selectedUsersStore.current?.map { selected -> selected.userId }.orEmpty()
                            val newInvites = if (currentSelectedAttendeeIds.isNotEmpty()) {
                                currentSelectedAttendeeIds.filterNot { userId ->
                                    initialAttendeeIds.contains(userId)
                                }.map { id ->
                                    EventInvitation(userId = id, remindBeforeMinutes = defaultEventReminders)
                                }
                            } else null
                            val obsoleteInvites = if (currentSelectedAttendeeIds.isNotEmpty()) {
                                initialAttendeeIds.filterNot { attendeeId ->
                                    currentSelectedAttendeeIds.contains(attendeeId)
                                }
                            } else initialAttendeeIds

                            // TODO Still only works via legacy api!
                            //  handle invitee changes directly via ObjectChange api here
                            activeObjectStore.updateAttendeesLegacyApi(Pair(newInvites, obsoleteInvites))
                            closeModal()
                        }
                    }
                }
            }
        }
    }
}

fun twTaskDueDateModal(
    toggleShowModalStore: Store<Boolean>,
    selectedDueDateHandler: SimpleHandler<String>
) {
    twSimpleModalWithCloseHeader(
        title = TL.CardDueDate.CARD_TITLE,
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full md:min-w-96 grow shrink overflow-y-auto p-2",
            id = "task-dueDate-modal",
        ) {
            withKoin {
                val activeObjectStore: ActiveObjectStore = get()
                val startTime = activeObjectStore.map(GeoObjectDetails.L.atTime)
                val datePickerStore = storeOf("")
                val timePickerStore = storeOf("")

                val dueDateFlow = startTime.data.map { start ->
                    if (start == null) {
                        val nextQuarterOfHour = Clock.System.now().nextQuarterOfHour()
                        // prefill fields and update stores with current time
                        datePickerStore.update(nextQuarterOfHour.formatDateForDatePicker())
                        timePickerStore.update(nextQuarterOfHour.formatTimeForTimePicker())
                        nextQuarterOfHour
                    } else {
                        datePickerStore.update(start.formatDateForDatePicker())
                        timePickerStore.update(start.formatTimeForTimePicker())
                        start
                    }
                }
                twContentScrollBox("gap-3") {
                    // DATE INPUT
                    inputLabelWrapper(
                        title = TL.CardDateTime.DATE,
                        visibilityFlow = dueDateFlow.map { it.formatDateForDatePicker().isNotBlank() },
                    ) {
                        genericInput(
                            value = dueDateFlow.map { it.formatDateForDatePicker() },
                            type = "date",
                        ) {
                            placeholder(TL.CardDateTime.DATE.getTranslationFlow())
                            changes.values() handledBy datePickerStore.update
                        }
                    }
                    // TIME INPUT
                    inputLabelWrapper(
                        title = TL.CardDateTime.TIME,
                        visibilityFlow = dueDateFlow.map { it.formatTimeForTimePicker().isNotBlank() },
                    ) {
                        genericInput(
                            value = dueDateFlow.map { it.formatTimeForTimePicker() },
                            type = "time",
                        ) {
                            placeholder(TL.CardDateTime.TIME.getTranslationFlow())
                            changes.values() handledBy timePickerStore.update
                        }
                    }
                }

                twPrimaryButton(
                    fallbackTextAsFlow = flowOf("Set Task Due Date"),
                ) {
                    clicks handledBy {
                        combineToInstant(datePickerStore.current, timePickerStore.current)?.toString()?.let {
                            selectedDueDateHandler(it)
                        }
                        closeModal()
                    }
                }
            }
        }
    }
}

fun RenderContext.taskStateIndicator(taskState: TaskState) {
    div("flex items-center justify-center grow-0 shrink-0 basis-8 w-8 h-8 text-formationBlack rounded-full p-2") {
        twIconMedium(taskState.getIcon())
        inlineStyle("--stateColor: ${taskState.getColor().color};")
        className("bg-[var(--stateColor)]")
    }
}

fun twTaskStateSelectModal(
    toggleShowModalStore: Store<Boolean>,
    selectedTaskStateHandler: SimpleHandler<TaskState>
) {
    twSimpleModalWithCloseHeader(
        title = TL.CardTaskState.CARD_TITLE,
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full md:min-w-96 overflow-y-auto p-2",
            id = "task-stateSelect-modal",
        ) {
            withKoin {
                val activeObjectStore: ActiveObjectStore = get()
                val objectTaskState = activeObjectStore.map(GeoObjectDetails.L.taskState)
                div(
                    "flex flex-col w-full h-64 gap-2 overflow-y-auto",
                ) {
                    TaskState.entries.forEach { taskState ->
                        if (taskState != TaskState.UnAssigned) {
                            div("flex flex-row w-full h-max items-center justify-between cursor-pointer hover:bg-gray-100 p-2 gap-2 rounded-xl") {
                                twRowOf {
                                    taskStateIndicator(taskState)
                                    titleSubtitleStack {
                                        twEllipseText("text-sm font-bold") {
                                            +taskState.getTitle()
                                        }
                                        twEllipseText("text-xs font-light") {
                                            +taskState.getSubtitle()
                                        }
                                    }
                                }
                                objectTaskState.data.render { activeState ->
                                    selectorIndicator(activeState == taskState)
                                }
                                clicks handledBy {
                                    selectedTaskStateHandler(taskState)
                                    optionalCloseHandlers?.let { handlers ->
                                        handlers.forEach {
                                            it.invoke()
                                        }
                                    }
                                    closeModal()
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

fun twMeetingDateTimeModal(
    toggleShowModalStore: Store<Boolean>,
    selectedAtTimeAndDurationHandler: SimpleHandler<Pair<String, Int>>
) {
    twSimpleModalWithCloseHeader(
        title = TL.CardDateTime.CARD_TITLE,
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full overflow-y-auto p-2",
            id = "meeting-dateTime-modal",
        ) {
            withKoin {
                val activeObjectStore: ActiveObjectStore = get()
                val startTime = activeObjectStore.map(GeoObjectDetails.L.atTime)
                val endTime = activeObjectStore.map(GeoObjectDetails.L.endTime)
                val datePickerStore = storeOf("")
                val timePickerStore = storeOf("")
                val durationPickerStore = storeOf(30)


                startTime.data.map { startTime ->
                    if (startTime == null) {
                        val nextQuarterOfHour = Clock.System.now().nextQuarterOfHour()
                        // prefill fields and update stores with current time
                        datePickerStore.update(nextQuarterOfHour.formatDateForDatePicker())
                        timePickerStore.update(nextQuarterOfHour.formatTimeForTimePicker())
                        durationPickerStore.update(30)
                        nextQuarterOfHour
                    } else {
                        datePickerStore.update(startTime.formatDateForDatePicker())
                        timePickerStore.update(startTime.formatTimeForTimePicker())
                        endTime.current?.let { endTime ->
                            durationPickerStore.update(extractDurationInMinutes(startTime, endTime).toInt())
                        }
                        startTime
                    }
                }

                twContentScrollBox("gap-3") {
                    // DATE INPUT
                    inputLabelWrapper(
                        title = TL.CardDateTime.DATE,
                        visibilityFlow = startTime.data.formatDateForDatePickerFlow().map { it.isNotBlank() },
                    ) {
                        genericInput(
                            value = startTime.data.formatDateForDatePickerFlow(),
                            type = "date",
                        ) {
                            placeholder(TL.CardDateTime.DATE.getTranslationFlow())
                            changes.values() handledBy datePickerStore.update
                        }
                    }
                    // TIME INPUT
                    inputLabelWrapper(
                        title = TL.CardDateTime.TIME,
                        visibilityFlow = formatTimeForTimePickerFlow(startTime.data).map { it.isNotBlank() },
                    ) {
                        genericInput(
                            value = formatTimeForTimePickerFlow(startTime.data),
                            type = "time",
                        ) {
                            placeholder(TL.CardDateTime.TIME.getTranslationFlow())
                            changes.values() handledBy timePickerStore.update
                        }
                    }
                    p("text-xs mt-3") {
                        translate(TL.CardDateTime.DURATION_IN_MINUTES)
                    }

                    // DURATION QUICK BUTTONS
                    twRowOfJustifyBetween {
                        smallSelectButton(isActive = durationPickerStore.data.map { it == 10 }) {
                            +"10 ${TL.CardDateTime.MINUTE.getString()}"
                            clicks.map { 10 } handledBy durationPickerStore.update
                        }
                        smallSelectButton(isActive = durationPickerStore.data.map { it == 20 }) {
                            +"20 ${TL.CardDateTime.MINUTE.getString()}"
                            clicks.map { 20 } handledBy durationPickerStore.update
                        }
                        smallSelectButton(isActive = durationPickerStore.data.map { it == 30 }) {
                            +"30 ${TL.CardDateTime.MINUTE.getString()}"
                            clicks.map { 30 } handledBy durationPickerStore.update
                        }
                    }
                    // DURATION INPUT
                    genericInput(
                        value = durationPickerStore.data.asString(),
                        type = "number",
                    ) {
                        inputs.values().map { it.toInt() } handledBy durationPickerStore.update
                    }
                }
                twPrimaryButton(
                    fallbackTextAsFlow = flowOf("Set Time and Duration"),
                ) {
                    clicks handledBy {
                        combineToInstant(datePickerStore.current, timePickerStore.current)?.toString()?.let { time ->
                            selectedAtTimeAndDurationHandler(Pair(time, durationPickerStore.current))
                        }
                        closeModal()
                    }
                }
            }
        }
    }
}

fun twMeetingInvitationStateModal(
    toggleShowModalStore: Store<Boolean>,
    selectedInvitationStateHandler: SimpleHandler<MeetingInvitationStatus>
) {
    twSimpleModalWithCloseHeader(
        title = TL.CardTaskState.CARD_TITLE, // TODO
        titleAsFlow = flowOf("Set Meeting State"),
        toggleStore = toggleShowModalStore,
    ) { closeModal, _, optionalCloseHandlers ->
        div(
            baseClass = "flex flex-col gap-3 w-full md:min-w-96 overflow-y-auto p-2",
            id = "meeting-dateTime-modal",
        ) {
            withKoin {
                val apiUserStore: ApiUserStore = get()
                val activeObjectStore: ActiveObjectStore = get()
                val attendees = activeObjectStore.map(GeoObjectDetails.L.attendees)

                val sortedStatusList = listOf(
                    MeetingInvitationStatus.Accepted,
                    MeetingInvitationStatus.Maybe,
                    MeetingInvitationStatus.Rejected,
                )

                // meeting invitation states
                div(
                    "flex flex-col w-full h-48 gap-2 overflow-y-auto",
                ) {
                    attendees.data.render { attendeeList ->
                        val usersStatus = attendeeList?.firstOrNull { it.userId == apiUserStore.current.userId }?.meetingInvitationStatus
                        sortedStatusList.forEach { state ->
                            div("flex flex-row w-full h-max items-center justify-between cursor-pointer hover:bg-gray-100 p-2 gap-2 rounded-xl") {
                                twRowOf {
                                    meetingStateIndicator(state)
                                    twEllipseText("text-sm font-bold") {
                                        +state.getName()
                                    }
                                }
                                selectorIndicator(state == usersStatus)

                                clicks handledBy {
                                    selectedInvitationStateHandler(state)
                                    optionalCloseHandlers?.let { handlers ->
                                        handlers.forEach {
                                            it.invoke()
                                        }
                                    }
                                    closeModal()
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
